import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Box, Card, LinearProgress, Stack, Typography,
} from '@mui/material';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import StackedLineChartOutlinedIcon from '@mui/icons-material/StackedLineChartOutlined';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';

import { formatPercent, formatTonne } from '../../../shared/utils/number';
import { SCOPE_TOKENS, TOKENS } from '../../../theme/muiTheme';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import useScope3Emissions from '../hooks/useScope3Emissions';
import CategoryCard from './CategoryCard';
import EmissionPanel from './EmissionPanel';
import EmissionOverviewNotice from './EmissionOverviewNotice';
import ScopeHero from './ScopeHero';
import ScopeTrendChart from './ScopeTrendChart';

const CATEGORY_COLORS = {
  cat1: { color: TOKENS.success, bg: TOKENS.successBg },
  cat2: { color: TOKENS.teal, bg: TOKENS.tealBg },
  cat3: { color: TOKENS.warning, bg: TOKENS.warningBg },
  cat5: { color: TOKENS.purple, bg: TOKENS.purpleBg },
  cat6: { color: SCOPE_TOKENS.SCOPE_1.color, bg: SCOPE_TOKENS.SCOPE_1.bg },
  cat7: { color: TOKENS.cyan, bg: TOKENS.cyanBg },
  cat8: { color: TOKENS.indigo, bg: TOKENS.indigoBg },
};

const emissionLevelForShare = (share) => {
  if (share >= 15) return 'high';
  if (share >= 7) return 'medium';
  return 'low';
};

const subcategoryCodeLabel = (code) => (
  /^1\.\d+$/.test(code) ? `Cat ${code}` : code
);

export default function Scope3Page() {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    year, setYear, years, data, loading, error, reload,
  } = useScope3Emissions();
  const scope = data.scope3;
  const isHistorical = Boolean(data.meta.isHistorical);
  const otherCategories = scope.categories.filter((category) => category.id !== 'cat1');
  const recordedCategoryCount = scope.categories.filter((category) => category.hasData).length;
  const hasData = data.meta.status.scope3 !== 'EMPTY';
  const anomalies = data.meta.anomalies.filter((item) => (
    item.startsWith('SCOPE3')
    || item.startsWith('MISSING_CAT5_SEAT')
    || item.startsWith('MISSING_CAT8')
    || /:(AF|CAT3|CAT5_SEAT)$/.test(item)
  ));
  usePagePresentation({
    loading,
    status: error ? null : data.meta.status.scope3,
    estimation: error ? null : data.meta.estimatedFactors.scope3,
  });
  const canRenderData = !error;
  const canRenderBreakdown = !error;
  const shareOfScope3 = (value) => (scope.total > 0 ? (value / scope.total) * 100 : 0);

  return (
    <Box component="section" aria-label={t('scopeEmissions.scope3.title')} className="page-stack">
      <ScopeHero
        title={t('scopeEmissions.scope3.title')}
        total={scope.total}
        year={year}
        years={years}
        onYearChange={setYear}
        tone={SCOPE_TOKENS.SCOPE_3}
        icon={PublicOutlinedIcon}
        yearLabel={t('scopeEmissions.common.reportingYear')}
        stats={[
          { value: recordedCategoryCount, label: t('scopeEmissions.scope3.categories') },
          { value: formatPercent(scope.shareOfTotal), label: t('scopeEmissions.scope3.ofTotal') },
        ]}
      />

      <EmissionOverviewNotice
        error={error}
        hasData={hasData}
        anomalies={anomalies}
        onRetry={reload}
      />

      {canRenderData && !isHistorical && (
      <EmissionPanel
        title={t('scopeEmissions.scope3.chartTitle')}
        icon={StackedLineChartOutlinedIcon}
        unit="tCO₂e"
      >
        <ScopeTrendChart
          data={scope.monthly}
          color={SCOPE_TOKENS.SCOPE_3.color}
          unit="tCO₂e"
          chartId="scope3-monthly-gradient"
          ariaLabel={t('scopeEmissions.scope3.chartAria', { year })}
        />
      </EmissionPanel>
      )}

      {canRenderBreakdown && (
      <Box component="section" aria-labelledby="scope3-category-title">
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <ViewModuleOutlinedIcon color="action" fontSize="small" />
          <Box>
            <Typography id="scope3-category-title" component="h2" variant="subtitle1" sx={{ fontWeight: 750 }}>
              {t('scopeEmissions.scope3.overviewTitle')}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
          }}
        >
          <Card
            component="section"
            aria-labelledby="scope3-cat1-title"
            sx={{
              gridColumn: '1 / -1',
              bgcolor: theme.palette.mode === 'dark'
                ? alpha(CATEGORY_COLORS.cat1.color, 0.1)
                : CATEGORY_COLORS.cat1.bg,
              borderColor: alpha(
                CATEGORY_COLORS.cat1.color,
                theme.palette.mode === 'dark' ? 0.45 : 0.65,
              ),
              borderStyle: scope.cat1.hasData ? 'solid' : 'dashed',
              opacity: scope.cat1.hasData ? 1 : 0.68,
              px: { xs: 1.75, md: 2.25 },
              py: { xs: 2, md: 2.25 },
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography variant="overline" sx={{ color: CATEGORY_COLORS.cat1.color, fontSize: 16, fontWeight: 900, textTransform: 'none', marginBottom: '8px' }}>
                  {scope.cat1.code}
                </Typography>
                <Typography id="scope3-cat1-title" component="h3" variant="subtitle1" sx={{ fontWeight: 750 }}>
                  {t(scope.cat1.labelKey)}
                </Typography>
              </Box>
              <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                <Typography
                  sx={{ color: CATEGORY_COLORS.cat1.color, fontSize: 25, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}
                >
                  {formatTonne(scope.cat1.value)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {scope.cat1.hasData
                    ? `${t('scopeEmissions.common.shareOfScope3', {
                      value: formatPercent(shareOfScope3(scope.cat1.value)),
                    })} · tCO₂e`
                    : `0 tCO₂e · ${t('scopeEmissions.common.noData')}`}
                </Typography>
              </Box>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={scope.cat1.hasData ? shareOfScope3(scope.cat1.value) : 0}
              sx={{
                bgcolor: alpha(CATEGORY_COLORS.cat1.color, 0.13),
                borderRadius: 99,
                height: 6,
                mb: 2,
                mt: 1.5,
                '& .MuiLinearProgress-bar': { bgcolor: CATEGORY_COLORS.cat1.color, borderRadius: 99 },
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: 'minmax(0, 1fr)',
                  sm: 'repeat(2, minmax(0, 1fr))',
                  lg: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1.25,
              }}
            >
              {scope.cat1.subcategories.map((subcategory) => {
                const share = scope.cat1.value > 0
                  ? (subcategory.value / scope.cat1.value) * 100
                  : 0;
                const emissionLevel = emissionLevelForShare(share);

                return (
                  <CategoryCard
                    key={subcategory.id}
                    code={subcategoryCodeLabel(subcategory.code)}
                     label={subcategory.labelKey && subcategory.labelKey.includes('.') ? t(subcategory.labelKey) : subcategory.labelKey}
                    value={subcategory.value}
                    total={scope.cat1.value}
                    color={CATEGORY_COLORS.cat1.color}
                    bg={CATEGORY_COLORS.cat1.bg}
                    shareLabel={t('scopeEmissions.scope3.subcategoryShare', {
                      value: formatPercent(share),
                    })}
                    compact
                    emissionLevel={subcategory.hasData ? emissionLevel : undefined}
                    emissionLevelLabel={subcategory.hasData
                      ? t(`scopeEmissions.scope3.emissionLevel.${emissionLevel}`)
                      : undefined}
                    hasData={subcategory.hasData}
                    noDataLabel={t('scopeEmissions.common.noData')}
                  />
                );
              })}
            </Box>
          </Card>

          {otherCategories.map((category) => {
              const palette = CATEGORY_COLORS[category.id] || { color: TOKENS.textSecondary, bg: 'background.paper' };
              return (
              <CategoryCard
                key={category.id}
                code={category.code}
                 label={category.labelKey && category.labelKey.includes('.') ? t(category.labelKey) : category.labelKey}
                value={category.value}
                total={scope.total}
                color={palette.color}
                bg={palette.bg}
                shareLabel={t('scopeEmissions.common.shareOfScope3', {
                  value: formatPercent(shareOfScope3(category.value)),
                })}
                hasData={category.hasData}
                noDataLabel={t('scopeEmissions.common.noData')}
              />
              );
            })}
        </Box>
      </Box>
      )}
    </Box>
  );
}
